import React, { useState, useEffect, useRef } from 'react';
import nimg from '../img/NIMGNN.png';
import nimg1 from '../img/NIMG1N.svg';
import nimg2 from '../img/BMLogo.png';
import lp2fm from '../img/LP2FM.png';
import lp2i1 from '../img/LP2I1.png';
import lp2i2 from '../img/LP2I2.png';
import lp2i3 from '../img/LP2I3.png';
import { ClaimFormSendEmails } from '../emailService';

const MAKE_WEBHOOK_URL = 'https://hook.eu2.make.com/7pynsgbax6gyoslgr58hqa67ag816wm5';
const META_PIXEL_ID = '2079831392804164';

const ClaimForm = () => {
  // --- STATE MANAGEMENT ---
  const [step, setStep] = useState(1);
  const [mobileStep, setMobileStep] = useState(1); // Separate step counter for mobile (1-3)
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [period, setPeriod] = useState('');
  const [isEligible, setIsEligible] = useState(null); // null: not checked, true: eligible, false: not eligible
  const [showEligibilityCheck, setShowEligibilityCheck] = useState(false);
  const [showEligibilitySuccess, setShowEligibilitySuccess] = useState(false);
  const [isMobileFormStarted, setIsMobileFormStarted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    involved: '',
    injured: '',
    fault: '',
    attorney: '',
    name: '',
    phone: '',
    email: '',
    terms: false
  });

  const [crmSubmissionId, setCrmSubmissionId] = useState(null);
  const [hasPhoneSubmitted, setHasPhoneSubmitted] = useState(false);
  const [lastEmailUpdate, setLastEmailUpdate] = useState('');
  const [lastFetchedSubmission, setLastFetchedSubmission] = useState(null);

  // Validation errors state
  const [errors, setErrors] = useState({
    name: '',
    phone: '',
    email: ''
  });

  const dropdownRef = useRef(null);
  // Ref to prevent multiple simultaneous API calls
  const isProcessingPhoneRef = useRef(false);
  
  // Ref to track if pixel has been fired for thank you page
  const thankYouPagePixelFired = useRef(false);

  // --- Meta Pixel Helpers ---

  /**
   * Format email for Meta Pixel advanced matching
   * Requirements: Lowercase unhashed (Meta will hash automatically)
   */
  const formatEmailForMeta = (email) => {
    if (!email) return null;
    return email.trim().toLowerCase();
  };

  /**
   * Format phone number for Meta Pixel advanced matching
   * Requirements: Digits only including country code and area code
   * Example: 16505554444 (1 for US country code + 10 digits)
   */
  const formatPhoneForMeta = (phone) => {
    if (!phone) return null;
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    // If 10 digits, assume US and add country code "1"
    if (digits.length === 10) {
      return `1${digits}`;
    }
    // If already has country code, return as is
    if (digits.length >= 10) {
      return digits;
    }
    return null;
  };

  /**
   * Parse full name into first and last name
   * Returns: { firstName: string, lastName: string }
   */
  const parseName = (fullName) => {
    if (!fullName) return { firstName: null, lastName: null };
    const trimmed = fullName.trim();
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) {
      return { firstName: parts[0].toLowerCase(), lastName: null };
    }
    const firstName = parts[0].toLowerCase();
    const lastName = parts.slice(1).join(' ').toLowerCase();
    return { firstName, lastName };
  };

  /**
   * Track Complete Registration event
   * Called when user clicks "Next Step" button
   */
  const trackMetaPixelCompleteRegistration = () => {
    if (typeof window === 'undefined') {
      console.warn('🔴 Meta Pixel: Window not available');
      return;
    }

    const pixelKey = META_PIXEL_ID;

    // Ensure pixel is initialized before tracking
    if (!window._metaPixelInitialized || !window._metaPixelInitialized[pixelKey]) {
      if (!window.fbq) {
        console.warn('🔴 Meta Pixel: fbq not available yet, will retry for pixel', META_PIXEL_ID);
        setTimeout(() => {
          if (window.fbq) {
            if (!window._metaPixelInitialized) window._metaPixelInitialized = {};
            window.fbq('init', pixelKey);
            window._metaPixelInitialized[pixelKey] = true;
            console.log('🟢 Meta Pixel: Retrying Complete Registration event for pixel', META_PIXEL_ID);
            if (window.fbq.loaded) {
              console.log('📡 API CALL: Complete Registration - Sending to Facebook Pixel (retry)', META_PIXEL_ID);
              console.log('📡 API CALL URL: https://www.facebook.com/tr/?id=' + META_PIXEL_ID + '&ev=Complete%20Registration');
              window.fbq('trackCustom', 'Complete Registration');
              console.log('✅ Meta Pixel: Complete Registration event tracked successfully (retry) for pixel', META_PIXEL_ID);
            }
          }
        }, 100);
        return;
      }
      console.log('🔵 Meta Pixel: Initializing pixel before Complete Registration for pixel', META_PIXEL_ID);
      window.fbq('init', pixelKey);
      if (!window._metaPixelInitialized) window._metaPixelInitialized = {};
      window._metaPixelInitialized[pixelKey] = true;
      console.log('✅ Meta Pixel: Pixel initialized for Complete Registration', META_PIXEL_ID);
    }

    if (!window.fbq) {
      console.warn('🔴 Meta Pixel: fbq not available yet');
      return;
    }

    // Wait for fbq to be loaded if not ready yet
    if (!window.fbq.loaded) {
      console.warn('⏳ Meta Pixel: fbq not loaded yet, waiting...');
      let retries = 0;
      const maxRetries = 20;
      const checkLoaded = setInterval(() => {
        retries++;
        if (window.fbq && window.fbq.loaded) {
          clearInterval(checkLoaded);
          console.log('🟢 Meta Pixel: Tracking Complete Registration event for pixel', META_PIXEL_ID);
          console.log('📡 API CALL: Complete Registration - Sending to Facebook Pixel', META_PIXEL_ID);
          console.log('📡 API CALL URL: https://www.facebook.com/tr/?id=' + META_PIXEL_ID + '&ev=Complete%20Registration');
          window.fbq('trackCustom', 'Complete Registration');
          console.log('✅ Meta Pixel: Complete Registration event tracked successfully for pixel', META_PIXEL_ID);
        } else if (retries >= maxRetries) {
          clearInterval(checkLoaded);
          console.error('❌ Meta Pixel: fbq failed to load after retries, tracking anyway');
          if (window.fbq) {
            console.log('📡 API CALL: Complete Registration - Sending to Facebook Pixel (retry)', META_PIXEL_ID);
            console.log('📡 API CALL URL: https://www.facebook.com/tr/?id=' + META_PIXEL_ID + '&ev=Complete%20Registration');
            window.fbq('trackCustom', 'Complete Registration');
          }
        }
      }, 100);
      return;
    }

    console.log('🟢 Meta Pixel: Tracking Complete Registration event for pixel', META_PIXEL_ID);
    console.log('📡 API CALL: Complete Registration - Sending to Facebook Pixel', META_PIXEL_ID);
    console.log('📡 API CALL URL: https://www.facebook.com/tr/?id=' + META_PIXEL_ID + '&ev=Complete%20Registration');
    // Use trackCustom for non-standard event name "Complete Registration"
    window.fbq('trackCustom', 'Complete Registration');
    console.log('✅ Meta Pixel: Complete Registration event tracked successfully for pixel', META_PIXEL_ID);
  };

  /**
   * Track Lead event with advanced matching data
   * Uses trackSingle to pass advanced matching without re-initializing pixel
   */
  const trackMetaPixelLead = (userData) => {
    if (typeof window === 'undefined') {
      console.warn('🔴 Meta Pixel: Window not available for Lead');
      return;
    }

    const pixelKey = META_PIXEL_ID;

    // Ensure pixel is initialized before tracking
    if (!window._metaPixelInitialized || !window._metaPixelInitialized[pixelKey]) {
      if (!window.fbq) {
        console.warn('🔴 Meta Pixel: fbq not available for Lead');
        return;
      }
      if (!window._metaPixelInitialized) window._metaPixelInitialized = {};
      window.fbq('init', pixelKey);
      window._metaPixelInitialized[pixelKey] = true;
      console.log('✅ Meta Pixel: Pixel initialized for Lead', META_PIXEL_ID);
    }

    if (!window.fbq) {
      console.warn('🔴 Meta Pixel: fbq not available for Lead');
      return;
    }

    // Helper function to actually track the Lead event
    const trackLeadEvent = () => {
      const advancedMatching = {};
      
      // Email: lowercase unhashed (Meta will hash automatically)
      if (userData.email) {
        const formattedEmail = formatEmailForMeta(userData.email);
        if (formattedEmail) {
          advancedMatching.em = formattedEmail;
        }
      }

      // Phone: digits only with country code
      if (userData.phone) {
        const formattedPhone = formatPhoneForMeta(userData.phone);
        if (formattedPhone) {
          advancedMatching.ph = formattedPhone;
        }
      }

      // First Name: lowercase letters
      if (userData.firstName) {
        advancedMatching.fn = userData.firstName.toLowerCase();
      }

      // Last Name: lowercase letters
      if (userData.lastName) {
        advancedMatching.ln = userData.lastName.toLowerCase();
      }

      // Track Lead event with advanced matching data
      // Use trackSingle to pass advanced matching without re-initializing the pixel
      if (Object.keys(advancedMatching).length > 0) {
        console.log('🟢 Meta Pixel: Tracking Lead event with advanced matching for pixel', META_PIXEL_ID, advancedMatching);
        console.log('📡 API CALL: Lead Event (Submit) - Sending to Facebook Pixel with advanced matching', META_PIXEL_ID);
        console.log('📡 API CALL URL: https://www.facebook.com/tr/?id=' + META_PIXEL_ID + '&ev=Lead');
        console.log('📡 API CALL DATA:', advancedMatching);
        window.fbq('trackSingle', META_PIXEL_ID, 'Lead', {}, advancedMatching);
        console.log('✅ Meta Pixel: Lead event tracked with advanced matching for pixel', META_PIXEL_ID);
      } else {
        console.log('🟢 Meta Pixel: Tracking Lead event (no advanced matching) for pixel', META_PIXEL_ID);
        console.log('📡 API CALL: Lead Event (Submit) - Sending to Facebook Pixel', META_PIXEL_ID);
        console.log('📡 API CALL URL: https://www.facebook.com/tr/?id=' + META_PIXEL_ID + '&ev=Lead');
        window.fbq('track', 'Lead');
        console.log('✅ Meta Pixel: Lead event tracked successfully for pixel', META_PIXEL_ID);
      }
    };

    // Wait for fbq to be loaded if not ready yet
    if (!window.fbq.loaded) {
      console.warn('⏳ Meta Pixel: fbq not loaded yet for Lead, waiting...');
      let retries = 0;
      const maxRetries = 20;
      const checkLoaded = setInterval(() => {
        retries++;
        if (window.fbq && window.fbq.loaded) {
          clearInterval(checkLoaded);
          trackLeadEvent();
        } else if (retries >= maxRetries) {
          clearInterval(checkLoaded);
          console.error('❌ Meta Pixel: fbq failed to load after retries, tracking anyway');
          if (window.fbq) {
            trackLeadEvent();
          }
        }
      }, 100);
      return;
    }

    trackLeadEvent();
  };

  // Initialize Meta Pixel on component mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Global flag to prevent multiple initializations and PageView tracking
    if (typeof window._metaPixelInitialized !== 'object') {
      window._metaPixelInitialized = {};
    }
    if (typeof window._metaPixelPageViewTracked !== 'object') {
      window._metaPixelPageViewTracked = {};
    }

    const pixelKey = META_PIXEL_ID;
    // Track PageView **per route** so each stepper form / route gets its own PageView
    const currentPath = typeof window !== 'undefined' ? window.location.pathname || '' : '';
    const pageKey = `${pixelKey}:${currentPath}`;
    let pageViewTracked = window._metaPixelPageViewTracked[pageKey] || false;

    // Function to initialize and track PageView
    const initAndTrack = () => {
      if (window.fbq && !pageViewTracked) {
        try {
          // Only init if not already initialized for this pixel ID
          if (!window._metaPixelInitialized[pixelKey]) {
            console.log('🔵 Meta Pixel: Initializing pixel', META_PIXEL_ID);
            window.fbq('init', META_PIXEL_ID);
            window._metaPixelInitialized[pixelKey] = true;
            console.log('✅ Meta Pixel: Pixel initialized successfully', META_PIXEL_ID);
          }
          // Only track PageView once per route (React SPA "page")
          if (!window._metaPixelPageViewTracked[pageKey]) {
            console.log('🔵 Meta Pixel: Tracking PageView for pixel', META_PIXEL_ID, 'on path', currentPath);
            window.fbq('track', 'PageView');
            window._metaPixelPageViewTracked[pageKey] = true;
            pageViewTracked = true;
            console.log('✅ Meta Pixel: PageView tracked successfully for pixel', META_PIXEL_ID, 'on path', currentPath);
          } else {
            console.log('⏭️ Meta Pixel: PageView already tracked for pixel', META_PIXEL_ID, 'on path', currentPath, '- skipping duplicate');
          }
        } catch (error) {
          console.error('❌ Meta Pixel tracking error:', error);
        }
      }
    };

    // Function to wait for fbq to be ready with retry
    const waitForFbq = (maxRetries = 10, retryDelay = 100) => {
      let retries = 0;
      const checkFbq = () => {
        if (window.fbq && window.fbq.loaded) {
          initAndTrack();
        } else if (retries < maxRetries) {
          retries++;
          setTimeout(checkFbq, retryDelay);
        } else {
          // Last attempt - try anyway if fbq exists
          if (window.fbq) {
            initAndTrack();
          }
        }
      };
      checkFbq();
    };

    // Check if already initialized and PageView tracked for this route - skip if so
    if (window._metaPixelInitialized[pixelKey] && window._metaPixelPageViewTracked[pageKey]) {
      return; // Already initialized and tracked for this "page", no need to do anything
    }

    // Check if Meta Pixel script is already in the DOM
    const existingScript = document.querySelector('script[src*="fbevents.js"]');
    
    if (existingScript) {
      console.log('📜 Meta Pixel: Script already exists in DOM');
      // Script already exists
      if (window.fbq && window.fbq.loaded) {
        // Script is loaded, initialize immediately
        console.log('✅ Meta Pixel: Script loaded, initializing immediately for pixel', META_PIXEL_ID);
        initAndTrack();
      } else {
        // Wait for script to be ready
        console.log('⏳ Meta Pixel: Script exists but not loaded yet, waiting...');
        waitForFbq();
      }
    } else {
      // Script doesn't exist, load it
      console.log('📥 Meta Pixel: Loading script for pixel', META_PIXEL_ID);
      (function(f,b,e,v,n,t,s) {
        if(f.fbq) {
          console.log('⚠️ Meta Pixel: fbq already exists, skipping script load');
          return;
        }
        console.log('📦 Meta Pixel: Initializing fbq function');
        n=f.fbq=function(){
          n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)
        };
        if(!f._fbq)f._fbq=n;
        n.push=n;
        n.loaded=!0;
        n.version='2.0';
        n.queue=[];
        t=b.createElement(e);
        t.async=!0;
        t.src=v;
        t.onload = function() {
          console.log('✅ Meta Pixel: Script loaded successfully for pixel', META_PIXEL_ID);
          // Script loaded, wait a bit then initialize and track
          setTimeout(() => {
            waitForFbq();
          }, 50);
        };
        t.onerror = function() {
          console.error('❌ Meta Pixel: Script failed to load for pixel', META_PIXEL_ID);
        };
        s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s);
        console.log('📤 Meta Pixel: Script tag inserted into DOM');
      })(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
    }
  }, []);

  // Track Meta Pixel Lead when thank you page appears
  useEffect(() => {
    if (isSubmitted && typeof window !== 'undefined' && !thankYouPagePixelFired.current) {
      // Use a small delay to ensure the thank you page is fully rendered
      const trackPixel = () => {
        if (!window.fbq) {
          // Retry if fbq is not ready yet (max 10 retries = 1 second)
          if (trackPixel.retries === undefined) trackPixel.retries = 0;
          if (trackPixel.retries < 10) {
            trackPixel.retries++;
            setTimeout(trackPixel, 100);
            return;
          } else {
            console.warn('Meta Pixel: fbq not available after retries');
            return;
          }
        }

        // Check if pixel is initialized - if not, wait a bit more
        if (!window._metaPixelInitialized || !window._metaPixelInitialized[META_PIXEL_ID]) {
          if (trackPixel.initRetries === undefined) trackPixel.initRetries = 0;
          if (trackPixel.initRetries < 5) {
            trackPixel.initRetries++;
            setTimeout(trackPixel, 200);
            return;
          }
        }

        try {
          // Mark as fired to prevent duplicate tracking
          thankYouPagePixelFired.current = true;

          // Ensure pixel is initialized before tracking
          if (!window._metaPixelInitialized || !window._metaPixelInitialized[META_PIXEL_ID]) {
            window.fbq('init', META_PIXEL_ID);
            if (!window._metaPixelInitialized) window._metaPixelInitialized = {};
            window._metaPixelInitialized[META_PIXEL_ID] = true;
          }
          
          // Track Lead event on thank you page (as per Meta Pixel Lead code)
          if (formData.name && (formData.email || formData.phone)) {
            const nameParts = parseName(formData.name);
            console.log('🎯 Meta Pixel: Tracking Lead event on thank you page with advanced matching');
            trackMetaPixelLead({
              email: formData.email,
              phone: formData.phone,
              firstName: nameParts.firstName,
              lastName: nameParts.lastName
            });
          } else {
            // Track Lead without advanced matching if user data not available
            console.log('🎯 Meta Pixel: Tracking Lead event on thank you page (no advanced matching)');
            window.fbq('track', 'Lead');
          }
        } catch (error) {
          console.error('Meta Pixel tracking error on thank you page:', error);
          // Reset flag on error so it can retry
          thankYouPagePixelFired.current = false;
        }
      };

      // Small delay to ensure DOM is ready and thank you page is visible
      setTimeout(trackPixel, 300);
    } else if (!isSubmitted) {
      // Reset the flag when form is reset (if user goes back)
      thankYouPagePixelFired.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitted]);

  const getTrustedFormDataFromDOM = () => {
    if (typeof document === 'undefined') return {};

    const certField = document.querySelector('[name="xxTrustedFormCertUrl"]');
    const pingField = document.querySelector('[name="xxTrustedFormPingUrl"]');
    const tokenField = document.querySelector('[name="xxTrustedFormCertToken"]');

    return {
      xxTrustedFormCertUrl: certField?.value || '',
      xxTrustedFormPingUrl: pingField?.value || '',
      xxTrustedFormCertToken: tokenField?.value || '',
    };
  };

  const getIPAddress = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data?.ip || '';
    } catch (error) {
      console.error('Failed to get IP address:', error);
      return '';
    }
  };

  const sendToMakeWebhook = async ({ name, email, phone, trustedFormCertUrl, ipAddress }) => {
    try {
      const res = await fetch(MAKE_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone,
          trustedFormCertUrl,
          ipAddress,
        }),
      });

      if (!res.ok) {
        // Don't block form submission; just log for debugging.
        console.error('Make webhook failed:', res.status, res.statusText);
      }
    } catch (err) {
      // Don't block form submission; just log for debugging.
      console.error('Make webhook error:', err);
    }
  };

  const sendToCRMBackend = async (formData, { method = 'POST', id = null } = {}) => {
    try {
      const payload = {
        countryName: 'USA',
        brandName: 'Claim4Accidents',
        websiteName: window.location.hostname || 'claim4accidents.com',
        formname: 'ClaimReview2',
        data: formData
      };

      const url = id
        ? `https://crm-internal-backend-ayb9fqawg8b6bjen.canadacentral-01.azurewebsites.net/api/submitformdata/${id}`
        : 'https://crm-internal-backend-ayb9fqawg8b6bjen.canadacentral-01.azurewebsites.net/api/submitformdata';

      console.log(`[CRM-CR2] Making ${method} request to:`, url, { id, hasId: !!id });

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        console.error(`[CRM-CR2] ${method} request failed`, res.status, res.statusText);
        const errorText = await res.text().catch(() => '');
        console.error('[CRM-CR2] Error response:', errorText);
        return null;
      }

      const json = await res.json().catch(() => ({}));
      console.log(`[CRM-CR2] ${method} request successful:`, json);
      return json;
    } catch (err) {
      console.error(`[CRM-CR2] ${method} request error:`, err);
      return null;
    }
  };

  // Check eligibility based on all answers
  const checkEligibility = () => {
    // Eligibility rules from spreadsheet:
    // 1. Were you involved? Yes = Eligible, No = Not eligible
    // 2. Were you injured? Yes = Eligible, No = Not eligible
    // 3. Were you legally at fault? Yes = Not eligible, No = Eligible
    // 4. Do you already have an attorney? Yes = Not eligible, No = Eligible
    // 5. When did the accident happen? All time periods = Eligible
    
    const eligible = 
      formData.involved === 'yes' &&  // Must be involved
      formData.injured === 'yes' &&   // Must be injured
      formData.fault === 'no' &&     // Must NOT be at fault
      formData.attorney === 'no' &&   // Must NOT have an attorney
      period !== '';                  // Time period must be selected (all are eligible)
    
    return eligible;
  };

  const fetchCrmSubmission = async (id) => {
    if (!id) {
      console.warn('[CRM-CR2] Cannot fetch: No ID provided');
      return null;
    }
    try {
      console.log('[CRM-CR2] Making GET call to fetch entry with ID:', id);
      const res = await fetch(`https://crm-internal-backend-ayb9fqawg8b6bjen.canadacentral-01.azurewebsites.net/api/submitformdata/id/${id}`);
      if (!res.ok) {
        console.error('[CRM-CR2] GET request failed:', res.status, res.statusText);
        return null;
      }
      const json = await res.json().catch(() => null);
      if (json?.data) {
        setLastFetchedSubmission(json.data);
        console.log('[CRM-CR2] GET successful! Entry data:', json.data);
      }
      return json?.data || null;
    } catch (err) {
      console.error('[CRM-CR2] GET request error:', err);
      return null;
    }
  };

  const triggerPhoneSubmission = async (digits) => {
    // Prevent multiple calls: check ref, state, and exact length
    if (isProcessingPhoneRef.current || hasPhoneSubmitted || !digits || digits.length !== 10) {
      return;
    }

    // Set processing flag immediately to prevent race conditions
    isProcessingPhoneRef.current = true;

    try {
      console.log('[CRM-CR2] Phone has 10 digits, making POST call to create entry...');

      const response = await sendToCRMBackend({
        name: formData.name,
        phone: digits,
        email: formData.email || '',
        involved: formData.involved,
        injured: formData.injured,
        fault: formData.fault,
        attorney: formData.attorney,
        period: period,
        tcpaConsent: false,
      }, { method: 'POST' });

      if (response?.id) {
        const uniqueId = response.id;
        console.log('[CRM-CR2] POST successful! Unique ID received:', uniqueId);

        setCrmSubmissionId(uniqueId);
        setHasPhoneSubmitted(true);

        console.log('[CRM-CR2] Fetching entry with GET call using unique ID:', uniqueId);
        const fetchedData = await fetchCrmSubmission(uniqueId);
        if (fetchedData) {
          console.log('[CRM-CR2] GET successful! Entry retrieved:', fetchedData);
        } else {
          console.warn('[CRM-CR2] GET failed - could not retrieve entry');
        }
      } else {
        console.error('[CRM-CR2] POST failed - no ID received in response:', response);
      }
    } finally {
      // Always reset the processing flag
      isProcessingPhoneRef.current = false;
    }
  };

  const triggerEmailUpdate = async (emailValue) => {
    const trimmed = emailValue.trim();
    if (!trimmed.toLowerCase().endsWith('.com')) return;
    if (trimmed === lastEmailUpdate) return;

    console.log('[CRM-CR2] Email ends with .com, checking for submission ID...');

    if (!crmSubmissionId && formData.phone.length === 10 && !hasPhoneSubmitted) {
      console.log('[CRM-CR2] No submission ID found, creating entry with phone first...');
      await triggerPhoneSubmission(formData.phone);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    if (!crmSubmissionId) {
      console.warn('[CRM-CR2] Cannot update email: No submission ID available. Phone may not have been submitted yet.');
      return;
    }

    console.log('[CRM-CR2] Making PUT call to update email in entry with unique ID:', crmSubmissionId);

    const response = await sendToCRMBackend({
      name: formData.name,
      phone: formData.phone,
      email: trimmed,
      involved: formData.involved,
      injured: formData.injured,
      fault: formData.fault,
      attorney: formData.attorney,
      period: period,
      tcpaConsent: true,
    }, { method: 'PUT', id: crmSubmissionId });

    if (response) {
      setLastEmailUpdate(trimmed);
      console.log('[CRM-CR2] PUT successful! Email updated in entry ID:', crmSubmissionId);

      const updatedData = await fetchCrmSubmission(crmSubmissionId);
      if (updatedData) {
        console.log('[CRM-CR2] Verified updated entry:', updatedData);
      }
    } else {
      console.error('[CRM-CR2] PUT failed - email update unsuccessful');
    }
  };

  // --- HANDLERS ---
  const handleNext = () => {
    if (isMobile) {
      // Mobile navigation logic
      if (mobileStep === 1) {
        // Validate first 4 questions (including attorney) before proceeding
        if (!formData.involved || !formData.injured || !formData.fault || !formData.attorney) {
          alert('Please answer all questions before proceeding.');
          return;
        }
        setMobileStep(2);
      } else if (mobileStep === 2) {
        // Validate accident timeframe
        if (!period) {
          alert('Please answer all questions before proceeding.');
          return;
        }
        // Track Complete Registration event
        trackMetaPixelCompleteRegistration();
        // Track Google Ads Sign-Up conversion
        if (typeof window !== 'undefined' && window.gtag_report_conversion) {
          window.gtag_report_conversion();
        }
        
        // Check eligibility
        const eligible = checkEligibility();
        setIsEligible(eligible);
        
        if (eligible) {
          // Show eligibility success overlay (full screen)
          setShowEligibilitySuccess(true);
        } else {
          setShowEligibilityCheck(true);
        }
      }
    } else {
      // Desktop navigation logic
      // Validate all step 1 fields before proceeding
      if (!formData.involved || !formData.injured || !formData.fault || !formData.attorney || !period) {
        alert('Please fill in all fields before proceeding to the next step.');
        return;
      }
      
      // Track Complete Registration event
      trackMetaPixelCompleteRegistration();
      // Track Google Ads Sign-Up conversion
      if (typeof window !== 'undefined' && window.gtag_report_conversion) {
        window.gtag_report_conversion();
      }
      
      // Check eligibility
      const eligible = checkEligibility();
      setIsEligible(eligible);
      
      if (eligible) {
        // Show eligibility success overlay (full screen)
        setShowEligibilitySuccess(true);
      } else {
        setShowEligibilityCheck(true);
      }
    }
  };

  const handlePrevious = () => {
    if (isMobile) {
      if (mobileStep > 1) {
        setMobileStep(mobileStep - 1);
      } else {
        handleBackToLanding();
      }
    } else {
      setStep(1);
    }
  };

  const handleSubmit = async () => {
    // Validate all fields
    const nameError = validateName(formData.name);
    const phoneError = validatePhone(formData.phone);
    const emailError = validateEmail(formData.email);

    // Update errors state
    setErrors({
      name: nameError,
      phone: phoneError,
      email: emailError
    });

    // Check if there are any validation errors
    if (nameError || phoneError || emailError) {
      alert('Please correct the errors in the form before submitting.');
      return;
    }

    if (!formData.terms) {
      alert('Please accept the terms and conditions');
      return;
    }

    setIsSubmitting(true);

    try {
      const trustedFormData = getTrustedFormDataFromDOM();
      const ipAddress = await getIPAddress();

      await sendToMakeWebhook({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        trustedFormCertUrl: trustedFormData.xxTrustedFormCertUrl || '',
        ipAddress,
      });

      // Check eligibility before submitting
      const eligible = checkEligibility();
      
      // Prepare email data - ensure all required fields are present and valid
      const emailData = {
        name: formData.name?.trim() || 'N/A',
        email: formData.email?.trim().toLowerCase() || '',
        phone: formData.phone?.trim() || 'N/A',
        involved: formData.involved === 'yes' ? 'Yes' : formData.involved === 'no' ? 'No' : 'Not answered',
        injured: formData.injured === 'yes' ? 'Yes' : formData.injured === 'no' ? 'No' : 'Not answered',
        fault: formData.fault === 'yes' ? 'Yes' : formData.fault === 'no' ? 'No' : 'Not answered',
        attorney: formData.attorney === 'yes' ? 'Yes' : formData.attorney === 'no' ? 'No' : 'Not answered',
        period: period || 'Not specified',
        eligibility_status: eligible ? 'eligible' : 'not-eligible',
        eligibility_status_text: eligible ? 'Eligible' : 'Not Eligible',
      };

      // Validate email before sending
      if (!emailData.email || !emailData.email.includes('@')) {
        console.error('Invalid email address:', emailData.email);
        alert('Please provide a valid email address.');
        setIsSubmitting(false);
        return;
      }

      // Send emails sequentially: admin first, then user if admin succeeds
      const result = await ClaimFormSendEmails(emailData, trustedFormData);

      // Log error details if sending failed
      if (!result.success) {
        console.error('EmailJS Error Details:', {
          error: result.error,
          emailData: emailData,
          trustedFormData: trustedFormData
        });
        
        // Check for specific EmailJS error codes
        if (result.error && result.error.includes('422')) {
          console.error('422 Unprocessable Entity - EmailJS template validation error');
          console.error('This usually means:');
          console.error('1. Missing required template variables');
          console.error('2. Invalid email format');
          console.error('3. Template variable type mismatch');
        }
      }

      if (result.success) {
        // Send data to CRM backend after EmailJS is successfully triggered
        await sendToCRMBackend({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          involved: formData.involved,
          injured: formData.injured,
          fault: formData.fault,
          attorney: formData.attorney,
          period: period,
          ipAddress: ipAddress,
          trustedFormCertUrl: trustedFormData.xxTrustedFormCertUrl || '',
          submissionDate: new Date().toISOString(),
          eligibilityStatus: eligible ? 'eligible' : 'not-eligible'
        });

        // Check eligibility after successful submission
        if (eligible) {
          // Note: Meta Pixel Lead event is tracked on thank you page, not here
          // This prevents duplicate tracking

          // Show success popup only if eligible
          setIsSubmitted(true);
        } else {
          // Show not eligible modal if not eligible
          setIsEligible(false);
          setShowEligibilityCheck(true);
          setIsSubmitting(false);
        }
      } else {
        // Show error message
        alert('Failed to submit form. Please try again later.');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred while submitting the form. Please try again.');
      setIsSubmitting(false);
    }
  };

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  const selectOption = (value) => {
    setPeriod(value);
    setDropdownOpen(false);
  };

  // Validation functions
  const validateName = (name) => {
    if (!name.trim()) {
      return 'Full name is required';
    }
    // Check if name contains numbers
    if (/\d/.test(name)) {
      return 'Full name should not contain numbers';
    }
    // Check if name has at least 2 characters
    if (name.trim().length < 2) {
      return 'Full name must be at least 2 characters';
    }
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone.trim()) {
      return 'Phone number is required';
    }
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    // Check if contains alphabets
    if (/[a-zA-Z]/.test(phone)) {
      return 'Phone number should only contain digits';
    }
    // Check if exactly 10 digits
    if (digitsOnly.length !== 10) {
      return 'Phone number must be exactly 10 digits';
    }
    return '';
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      return 'Email address is required';
    }
    // Check if email contains @ symbol
    if (!email.includes('@')) {
      return 'Email must contain @ symbol';
    }
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const handleInputChange = (field, value) => {
    let updatedValue = value;
    let error = '';
    
    switch (field) {
      case 'name':
        // Allow only letters, spaces, hyphens, and apostrophes
        updatedValue = value.replace(/[^a-zA-Z\s'-]/g, '');
        error = validateName(updatedValue);
        break;
      case 'phone':
        // Only allow digits, limit to 10 digits
        const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
        updatedValue = digitsOnly;
        // Only validate if there's a value
        error = digitsOnly.length > 0 ? validatePhone(digitsOnly) : '';
        // Trigger submission only when exactly 10 digits are reached
        if (digitsOnly.length === 10) {
          triggerPhoneSubmission(digitsOnly);
        }
        break;
      case 'email':
        error = validateEmail(value);
        break;
      default:
        break;
    }
    
    // Update form data
    setFormData({ ...formData, [field]: updatedValue });
    
    // Update errors
    setErrors({ ...errors, [field]: error });
  };

  const handleRadioChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleCheckboxChange = (checked) => {
    setFormData({ ...formData, terms: checked });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 992);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handlers for mobile navigation
  const handleStartClaim = () => {
    // Track Complete Registration event
    trackMetaPixelCompleteRegistration();
    // Track Google Ads Sign-Up conversion
    if (typeof window !== 'undefined' && window.gtag_report_conversion) {
      window.gtag_report_conversion();
    }
    setIsMobileFormStarted(true);
  };

  const handleBackToLanding = () => {
    setIsMobileFormStarted(false);
    setStep(1);
  };


  // --- STYLES OBJECTS (Inline CSS) ---
  const styles = {
    body: {
      fontFamily: "'Roboto', sans-serif",
      backgroundColor: '#f4f6f9',
      display: 'flex',
      justifyContent: 'center',
      alignItems: isMobile ? 'flex-start' : 'center',
      minHeight: '100vh',
      height: isMobile ? 'auto' : '100vh',
      padding: isMobile ? '0' : '20px',
      transition: 'background-color 0.5s ease',
      width: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden',
      overflowY: isMobile ? 'auto' : 'hidden'
    },
    container: {
      display: 'flex',
      width: '1400px',
      maxWidth: '100%',
      gap: isMobile ? '0' : '30px',
      alignItems: 'stretch',
      margin: '0 auto',
      transition: 'opacity 0.3s',
      flexDirection: isMobile ? 'column' : 'row'
    },
    // Left Panel
    leftPanel: {
      flex: 1,
      backgroundColor: '#E7E8EE',
      padding: '40px 30px',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      borderRadius: '10px',
      boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
      height: 'auto'
    },
    logo: {
      fontWeight: 800,
      color: '#1a237e',
      fontSize: '1.3rem',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      letterSpacing: '0.5px'
    },
    logoContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      gap: '15px'
    },
    logoIcon: {
      width: '10rem',
      height: '3rem',
      flexShrink: 0,
      objectFit: 'contain'
    },
    logoText: {
      fontWeight: 800,
      color: '#1a237e',
      fontSize: '1.3rem',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      letterSpacing: '0.5px',
      flex: 1,
      justifyContent: 'center'
    },
    logoSpan: { color: '#e64a19' },
    h1: {
      color: '#0A1F8F',
      textAlign: 'center',
      fontFamily: '"Raleway", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: isMobile ? '24px' : '35px',
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: 'normal',
      marginBottom: '10px'
    },
    subHeader: {
      color: '#0A1F8F',
      textAlign: 'center',
      fontFamily: '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: isMobile ? '0.9rem' : '18px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: 'normal',
      letterSpacing: '0.36px',
      textTransform: 'uppercase',
      marginBottom: '20px'
    },
    description: {
      color: '#444',
      textAlign: 'center',
      fontFamily: '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: isMobile ? '14px' : '16px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 'normal',
      marginBottom: isMobile ? '15px' : '30px',
      maxWidth: '90%'
    },
    benefits: {
      display: 'flex',
      justifyContent: 'center',
      gap: isMobile ? '5px' : '10px',
      marginBottom: '30px',
      width: '100%',
      flexWrap: 'nowrap'
    },
    benefitItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '0.7rem',
      color: '#1a237e',
      fontWeight: 700,
      background: 'transparent',
      padding: isMobile ? '8px 5px' : '10px 12px',
      borderRadius: '6px',
      boxShadow: 'none',
      textAlign: 'left',
      flex: 1,
      minWidth: isMobile ? '80px' : '110px',
      justifyContent: 'center',
      lineHeight: 1.2
    },
    statuteBox: { marginBottom: '20px', zIndex: 1 },
    statuteTitle: {
      color: '#0A1F8F',
      textAlign: 'center',
      fontFamily: '"Raleway", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: '25px',
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: 'normal',
      marginBottom: '5px'
    },
    statuteText: {
      color: '#444',
      textAlign: 'center',
      fontFamily: '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: '15px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 'normal'
    },
    carImage: {
      width: '100%',
      height: '130px',
      backgroundImage: "url('https://images.unsplash.com/photo-1599256621730-d3169f17a027?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      borderRadius: '8px',
      marginBottom: '20px',
      maskImage: 'linear-gradient(to bottom, black 40%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(to bottom, black 40%, transparent 100%)'
    },
    // Right Panel
    rightPanel: {
      flex: 1,
      backgroundColor: isMobile ? '#f4f6f9' : '#E7E8EE',
      padding: isMobile ? '0' : '40px 50px',
      borderRadius: isMobile ? '0' : '10px',
      boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      minHeight: isMobile ? '100vh' : 'auto'
    },
    stepIndicator: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '8px',
      width: '100%',
      textAlign: 'right',
      marginBottom: isMobile ? '12px' : '25px',
      color: '#1a237e',
      fontWeight: 800,
      fontSize: isMobile ? '0.95rem' : '1.1rem'
    },
    stepCount: { color: '#546e7a', fontWeight: 600, fontSize: isMobile ? '0.85rem' : '0.95rem' },
    formGroup: { marginBottom: isMobile ? '30px' : '20px' },
    formLabel: {
      display: 'block',
      color: '#0A1F8F',
      fontWeight: 700,
      marginBottom: isMobile ? '15px' : '10px',
      fontSize: isMobile ? '1.3rem' : '1rem',
      fontFamily: '"Raleway", sans-serif',
      lineHeight: 1.2
    },
    formInput: {
      width: '100%',
      padding: '12px 15px',
      border: '1px solid #e0e0e0',
      borderRadius: '6px',
      fontSize: '0.9rem',
      color: '#333',
      background: '#f9f9f9',
      transition: 'border 0.2s',
      outline: 'none',
      boxSizing: 'border-box'
    },
    formInputError: {
      width: '100%',
      padding: '12px 15px',
      border: '1px solid #e64a19',
      borderRadius: '6px',
      fontSize: '0.9rem',
      color: '#333',
      background: '#fff5f5',
      transition: 'border 0.2s',
      outline: 'none',
      boxSizing: 'border-box'
    },
    floatingLabelGroup: {
      position: 'relative',
      marginBottom: '20px',
      width: '100%'
    },
    floatingLabel: {
      position: 'absolute',
      left: '15px',
      color: '#666',
      fontWeight: 400,
      fontSize: '1rem',
      top: '18px',
      transition: 'all 0.2s ease-in-out',
      pointerEvents: 'none',
      zIndex: 1,
      fontFamily: '"Raleway", sans-serif'
    },
    floatingLabelActive: {
      top: '8px',
      fontSize: '0.75rem',
      color: '#0A1F8F',
      fontWeight: 700
    },
    floatingInput: {
      paddingTop: '24px',
      paddingBottom: '8px'
    },
    errorMessage: {
      color: '#e64a19',
      fontSize: '0.75rem',
      marginTop: '5px',
      display: 'block',
      fontWeight: 500
    },
    radioOptions: { 
      display: 'flex', 
      gap: isMobile ? '10px' : '20px',
      flexDirection: isMobile ? 'row' : 'row'
    },
    radioOption: { 
      flex: 1, 
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: '0',
      borderRadius: isMobile ? '8px' : '4px',
      border: isMobile ? '1px solid #e0e0e0' : 'none',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      boxShadow: isMobile ? '0 1px 3px rgba(0,0,0,0.05)' : 'none'
    },
    radioOptionSelected: {
      borderColor: '#0A1F8F',
      backgroundColor: '#ffffff',
    },
    // Custom Select
    customSelectWrapper: { position: 'relative', userSelect: 'none', width: '100%', zIndex: 100 },
    customSelectTrigger: {
      position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '8px 10px 8px 15px', fontSize: '0.95rem', fontWeight: 500, color: '#333',
      background: '#ffffff', border: dropdownOpen ? '1.5px solid #1a237e' : '1px solid #cfd8dc',
      borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s', height: '50px'
    },
    triggerArrow: {
      background: '#1a237e', width: '30px', height: '30px', display: 'flex',
      alignItems: 'center', justifyContent: 'center', borderRadius: '4px',
      transition: 'transform 0.3s',
      transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
    },
    customOptions: {
      position: 'absolute', display: 'block', top: '100%', left: 0, right: 0, background: '#fff',
      transition: 'all 0.2s ease-in-out',
      opacity: dropdownOpen ? 1 : 0,
      visibility: dropdownOpen ? 'visible' : 'hidden',
      pointerEvents: dropdownOpen ? 'all' : 'none',
      transform: dropdownOpen ? 'translateY(0)' : 'translateY(10px)',
      zIndex: 200, marginTop: '5px', borderRadius: '6px',
      boxShadow: '0 10px 20px rgba(0,0,0,0.15)', border: '1px solid #e0e0e0', overflow: 'hidden'
    },
    btnPrimary: {
      backgroundColor: '#0A1F8F',
      color: 'white',
      border: 'none',
      padding: isMobile ? '12px 20px' : '16px',
      borderRadius: '6px',
      fontWeight: 700,
      cursor: 'pointer',
      width: '100%',
      height: isMobile ? '50px' : '60px',
      fontSize: isMobile ? '0.9rem' : '1rem',
      marginTop: isMobile ? '5px' : '10px',
      transition: 'background 0.3s',
      display: 'block',
      textAlign: 'center',
      boxSizing: 'border-box'
    },
    buttonContainer: {
      display: 'flex',
      gap: isMobile ? '10px' : '15px',
      marginTop: 'auto',
      width: '100%',
      paddingTop: isMobile ? '0px' : '20px',
      paddingBottom: isMobile ? '15px' : '0',
      marginBottom: isMobile ? '-10px' : '0',
      boxSizing: 'border-box',
    },
    stepContainer: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      width: '100%'
    },
    btnPrevious: {
      backgroundColor: '#0A1F8F',
      color: 'white',
      border: 'none',
      padding: isMobile ? '12px 20px' : '16px',
      borderRadius: '6px',
      fontWeight: 700,
      cursor: 'pointer',
      flex: 1,
      height: isMobile ? '50px' : '60px',
      fontSize: isMobile ? '0.9rem' : '1rem',
      transition: 'background 0.3s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      boxSizing: 'border-box'
    },
    btnSubmit: {
      backgroundColor: '#0A1F8F',
      color: 'white',
      border: 'none',
      padding: isMobile ? '12px 20px' : '16px',
      borderRadius: '6px',
      fontWeight: 700,
      cursor: 'pointer',
      flex: 1,
      height: isMobile ? '50px' : '60px',
      fontSize: isMobile ? '0.9rem' : '1rem',
      transition: 'background 0.3s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      boxSizing: 'border-box'
    },
    // Step 2 specifics
    step2Header: {
      color: '#0A1F8F',
      textAlign: 'center',
      fontFamily: 'Raleway',
      fontSize: isMobile ? '1.3rem' : '28px',
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: 'normal',
      marginBottom: isMobile ? '5px' : '5px'
    },
    step2Subheader: {
      color: '#444',
      textAlign: 'center',
      fontFamily: '"Segoe UI"',
      fontSize: isMobile ? '0.9rem' : '16px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 'normal',
      marginBottom: isMobile ? '15px' : '25px'
    },
    checkboxGroup: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      marginBottom: isMobile ? '12px' : '20px',
      marginTop: isMobile ? '5px' : '10px'
    },
    legalText: { fontSize: isMobile ? '0.6rem' : '0.65rem', color: '#888', lineHeight: 1.4, textAlign: 'justify' },
    // Timeframe options (mobile step 2)
    timeframeOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px 14px',
      borderRadius: '10px',
      border: '1px solid #cfd8dc',
      backgroundColor: '#ffffff',
      marginBottom: '10px',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    },
    timeframeOptionSelected: {
      backgroundColor: '#1a237e',
      borderColor: '#1a237e',
      color: '#ffffff',
      boxShadow: '0 4px 10px rgba(26,35,126,0.35)'
    },
    timeframeRadioCircle: {
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      border: '2px solid #cfd8dc',
      backgroundColor: '#ffffff',
      boxSizing: 'border-box'
    },
    timeframeRadioCircleSelected: {
      borderColor: '#ffffff',
      backgroundColor: '#ffffff',
      boxShadow: '0 0 0 4px rgba(255,255,255,0.4)'
    },
    
    // Success Overlay
    successOverlay: {
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: '#E7E8EE',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 1000, 
      opacity: isSubmitted ? 1 : 0, 
      visibility: isSubmitted ? 'visible' : 'hidden', 
      transition: 'opacity 0.5s ease',
      padding: '20px'
    },
    // Eligibility Success Overlay
    eligibilitySuccessOverlay: {
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: '#E7E8EE',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 1000, 
      opacity: showEligibilitySuccess && isEligible === true ? 1 : 0, 
      visibility: showEligibilitySuccess && isEligible === true ? 'visible' : 'hidden', 
      transition: 'opacity 0.5s ease',
      padding: '20px'
    },
    eligibilitySuccessCard: {
      background: 'white', padding: '40px', borderRadius: '12px', textAlign: 'center',
      width: '400px', maxWidth: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
      transform: showEligibilitySuccess && isEligible === true ? 'translateY(0)' : 'translateY(20px)', 
      transition: 'transform 0.5s ease'
    },
    // Not Eligible Overlay
    notEligibleOverlay: {
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: '#E7E8EE',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 1000, 
      opacity: showEligibilityCheck && isEligible === false ? 1 : 0, 
      visibility: showEligibilityCheck && isEligible === false ? 'visible' : 'hidden', 
      transition: 'opacity 0.5s ease',
      padding: '20px'
    },
    notEligibleCard: {
      background: 'white', padding: '40px', borderRadius: '12px', textAlign: 'center',
      width: '400px', maxWidth: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
      transform: showEligibilityCheck && isEligible === false ? 'translateY(0)' : 'translateY(20px)', 
      transition: 'transform 0.5s ease'
    },
    successCard: {
      background: 'white', padding: '40px', borderRadius: '12px', textAlign: 'center',
      width: '400px', maxWidth: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
      transform: isSubmitted ? 'translateY(0)' : 'translateY(20px)', 
      transition: 'transform 0.5s ease'
    },
    iconBox: {
      background: '#f0f4f8', width: '70px', height: '70px', borderRadius: '12px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 20px auto', position: 'relative'
    },
    checkBadge: {
      position: 'absolute', bottom: '-5px', right: '-5px', background: '#1a237e',
      color: 'white', width: '24px', height: '24px', borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: '2px solid white', fontSize: '0.7rem'
    },
    // Mobile specific styles
    startClaimButton: {
      backgroundColor: '#1a237e',
      color: 'white',
      border: 'none',
      padding: '16px 32px',
      borderRadius: '8px',
      fontWeight: 700,
      cursor: 'pointer',
      height: isMobile ? '50px' : '60px',
      fontSize: '1rem',
      transition: 'background 0.3s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      width: '100%',
      marginTop: isMobile ? '0px' : '20px',
      boxShadow: '0 4px 12px rgba(26, 35, 126, 0.3)'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#1a237e',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: 600,
      padding: '8px 0',
      textDecoration: 'none'
    },
    mobileHeader: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      padding: '20px',
      marginBottom: '20px',
      borderBottom: '1px solid #e0e0e0'
    }
  };

  // Auto-navigate from eligibility success screen to form after 1 seconds
  useEffect(() => {
    if (showEligibilitySuccess && isEligible === true) {
      const timer = setTimeout(() => {
        setShowEligibilitySuccess(false);
        if (isMobile) {
          setMobileStep(3);
        } else {
          setStep(2);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showEligibilitySuccess, isEligible, isMobile]);

  // --- RAW CSS FOR MEDIA QUERIES & PSEUDO ELEMENTS ---
  const mediaQueriesAndPseudo = `
    * { box-sizing: border-box; }
    @keyframes crSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
    
    /* Font Family Declarations */
    .left-panel-h1,
    .left-panel h1 {
      font-family: "Raleway", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    .left-panel-subheader,
    .left-panel .subHeader {
      font-family: "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
    }
    .left-panel-description,
    .left-panel .description,
    .left-panel p {
      font-family: "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
    }
    .left-panel-statute-title,
    .left-panel .statuteTitle {
      font-family: "Raleway", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    .left-panel-statute-text,
    .left-panel .statuteText {
      font-family: "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
    }
    
    /* Pseudo-classes replacements */
    .btn-primary:hover { background-color: #0d156b !important; }
    .btn-previous:hover { background-color: #0d156b !important; }
    .btn-submit:hover { background-color: #0d156b !important; }
    .form-input:focus { border-color: #1a237e !important; background: #fff !important; }
    
    /* Radio Styling Logic */
    .radio-option input[type="radio"] { display: none; }
    .radio-option label {
        display: flex; align-items: center; padding: 12px 15px;
        background: #fff; border: 1px solid #e0e0e0; border-radius: 6px;
        cursor: pointer; font-size: 0.9rem; color: #455a64;
        transition: all 0.2s; font-weight: 500; width: 100%;
        box-sizing: border-box;
        position: relative;
    }
    .radio-option label::before {
        content: ''; width: 18px; height: 18px; border: 2px solid #b0bec5;
        border-radius: 50%; margin-right: 12px; background: white; transition: all 0.2s; flex-shrink: 0;
    }
    .radio-option input[type="radio"]:checked + label {
        background-color: #fff; color: #0A1F8F; border-color: #0A1F8F;
        box-shadow: 0 2px 8px rgba(10, 31, 143, 0.1);
    }
    .radio-option input[type="radio"]:checked + label::before {
        background-color: #0A1F8F; 
        border: 3px solid white;
        box-shadow: 0 0 0 1px #0A1F8F, 0 0 4px rgba(255, 255, 255, 0.8);
    }

    /* Custom Option Hover & Select */
    .custom-option {
        position: relative; display: flex; align-items: center; padding: 14px 20px;
        font-size: 0.95rem; color: #37474f; cursor: pointer; transition: background 0.2s;
        border-bottom: 1px solid #f5f5f5;
    }
    .custom-option:hover { background-color: #f5f7fa; }
    .custom-option:last-child { border-bottom: none; }
    .custom-option .radio-circle {
        width: 18px; height: 18px; border: 2px solid #b0bec5; border-radius: 50%;
        margin-right: 15px; position: relative; flex-shrink: 0;
    }
    .custom-option.selected { background-color: #e8eaf6; color: #1a237e; font-weight: 500; }
    .custom-option.selected .radio-circle { border-color: #1a237e; border-width: 2px; }
    .custom-option.selected .radio-circle::after {
        content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
        width: 10px; height: 10px; background-color: #1a237e; border-radius: 50%;
    }

    /* --- RESPONSIVE MEDIA QUERIES --- */
    @media (max-width: 992px) {
        .container { flex-direction: column; width: 100%; gap: 0; }
        .left-panel, .right-panel { width: 100% !important; max-width: 100%; margin: 0; border-radius: 0 !important; }
        .left-panel { min-height: 100vh; padding: 20px 0 !important; }
        .right-panel { min-height: 100vh; padding: 15px 0 !important; }
    }
    @media (max-width: 600px) {
        .main-body-wrapper { padding: 0 !important; }
        .container { gap: 0; }
        .left-panel, .right-panel { padding: 12px 0 !important; }
        .radio-options { gap: 10px !important; } 
        .radio-option label { padding: 10px 12px !important; font-size: 14px !important; }
        .custom-option { padding: 10px 15px !important; }
        .success-card { padding: 30px 20px !important; }
    }
    @media (max-width: 400px) {
        .benefits { flex-direction: row; }
        .radio-option label { padding: 8px 10px; font-size: 0.8rem; }
        .form-label { font-size: 0.85rem; }
    }
  `;

  return (
    <div style={styles.body} className="main-body-wrapper">
      {/* External Resources */}
      <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&family=Raleway:wght@400;600;700;800&display=swap" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      <style>{mediaQueriesAndPseudo}</style>

      {/* --- ELIGIBILITY SUCCESS VIEW --- */}
      <div style={styles.eligibilitySuccessOverlay}>
        <div style={styles.eligibilitySuccessCard} className="eligibility-success-card">
          {/* Green Checkmark Circle */}
          <div style={{
            width: '70px', 
            height: '70px', 
            borderRadius: '50%',
            border: '4px solid #4CAF50',
            background: 'white',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 20px auto'
          }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#4CAF50" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          
          {/* Congratulations Text */}
          <div style={{
            color: '#D14836',
            textAlign: 'center',
            fontFamily: 'Raleway, system-ui, sans-serif',
            fontSize: '25px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '35px',
            letterSpacing: '1px',
            marginBottom: '10px'
          }}>Congratulations!</div>
          
          {/* You're Eligible Text */}
          <div style={{
            color: '#0A1F8F',
            fontFamily: 'Raleway, system-ui, sans-serif',
            fontSize: '25px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '35px',
            letterSpacing: '1px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            You're eligible!
          </div>
          
          {/* Description Text */}
          <div style={{
            color: '#0A1F8F',
            textAlign: 'center',
            fontFamily: '"Rethink Sans", system-ui, sans-serif',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: 'normal',
            marginBottom: '25px'
          }}>
            Please provide your details so we can review your options and proceed.
          </div>
          
          {/* Please Wait Button with Spinner */}
          <button
            disabled
            style={{
              ...styles.btnPrimary,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              background: 'rgba(209, 72, 54, 0.20)',
              color: 'white',
              cursor: 'not-allowed',
              opacity: 0.9
            }}
          >
            <div style={{
              width: '20px',
              height: '20px',
              border: '3px solid rgba(255, 255, 255, 0.3)',
              borderTop: '3px solid white',
              borderRadius: '50%',
              animation: 'crSpin 1s linear infinite'
            }}></div>
            <span>Please wait...</span>
          </button>
        </div>
      </div>

      {/* --- SUCCESS VIEW --- */}
      <div style={styles.successOverlay}>
        <div style={styles.successCard} className="success-card">
          <div style={styles.iconBox}>
            <i className="fa-regular fa-file-lines" style={{fontSize: '2rem', color: '#b0bec5'}}></i>
            <div style={styles.checkBadge}><i className="fa-solid fa-check"></i></div>
          </div>
          <div style={{color: '#e64a19', fontSize: '1.2rem', fontWeight: 700, marginBottom: '5px'}}>Thank You!</div>
          <div style={{color: '#1a237e', fontSize: '1.1rem', fontWeight: 800, marginBottom: '15px', lineHeight: 1.3}}>
            Your Form has been submitted Successfully.
          </div>
          <div style={{color: '#666', fontSize: '0.8rem', marginBottom: '25px'}}>
            Please be patient, Our team will contact you shortly.
          </div>
          <div style={{background: '#dce3e9', color: '#1a237e', padding: '12px', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600}}>
            Redirecting to Home page....
          </div>
        </div>
      </div>

      {/* --- NOT ELIGIBLE VIEW --- */}
      <div style={styles.notEligibleOverlay}>
        <div style={styles.notEligibleCard} className="not-eligible-card">
          <div style={{
            width: '70px', 
            height: '70px', 
            borderRadius: '50%',
            border: '4px solid #E53935',
            background: 'white',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 20px auto'
          }}>
            <i className="fa-solid fa-times" style={{fontSize: '2rem', color: '#E53935', fontWeight: 'bold'}}></i>
          </div>
          <div style={{color: '#E53935', fontSize: '1.2rem', fontWeight: 700, marginBottom: '5px'}}>Sorry!</div>
          <div style={{color: '#1a237e', fontSize: '1.5rem', fontWeight: 800, marginBottom: '15px', lineHeight: 1.3}}>
            You're not eligible!
          </div>
          <div style={{color: '#666', fontSize: '0.9rem', marginBottom: '25px', lineHeight: 1.6}}>
            You're not eligible for this claim, but you may qualify for other assistance options.
          </div>
          <button 
            type="button"
            className="btn-primary" 
            style={styles.btnPrimary}
            onClick={() => {
              setShowEligibilityCheck(false);
              setShowEligibilitySuccess(false);
              setIsEligible(null);
              // Reset form
              setFormData({
                involved: '',
                injured: '',
                fault: '',
                attorney: '',
                name: '',
                phone: '',
                email: '',
                terms: false
              });
              setPeriod('');
              setStep(1);
              setMobileStep(1);
            }}
          >
            <i className="fa-solid fa-arrow-left"></i> PREVIOUS
          </button>
        </div>
      </div>

      {/* --- MAIN CONTAINER --- */}
      {!isSubmitted && (
        <div className="container" style={styles.container}>
          
          {/* --- LEFT PANEL --- */}
          <div 
            className="left-panel" 
            style={{
              ...styles.leftPanel,
              display: (isMobile && isMobileFormStarted) ? 'none' : 'flex',
              ...(isMobile && !isMobileFormStarted && { 
                minHeight: '100vh',
                textAlign: 'center',
                alignItems: 'center',
                backgroundImage: `url(${lp2fm})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundColor: 'transparent'
              })
            }}
          >
            <div style={styles.logoContainer}>
              {isMobile ? (
                <>
                  <img 
                    src="/static/media/NIMG1N.2317fbcd248a0fc2b1b7e6e2031c31de.svg" 
                    alt="Logo" 
                    style={{width: '10rem', height: '3rem', flexShrink: 0, objectFit: 'contain'}} 
                  />
                  <img 
                    src="data:image/png;base64,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" 
                    alt="Logo" 
                    style={{width: '130px', height: 'auto', flexShrink: 0, objectFit: 'contain'}} 
                  />
                </>
              ) : (
                <>
                  <img src={nimg1} alt="Logo" style={styles.logoIcon} />
                  <img src={nimg2} alt="Logo" style={styles.logoIcon} />
                </>
              )}
            </div>

            {isMobile && !isMobileFormStarted ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                margin: '0 auto',
                padding: '20px',
                position: 'relative',
                transform: 'translateY(50px)',
                gap:'5px'
              }}>
                <h1
                  className="left-panel-h1"
                  style={{
                    ...styles.h1,
                    textAlign: 'center',
                    width: '100%',
                    marginBottom: '15px'
                  }}
                >
                  INJURED IN A CAR ACCIDENT?
                </h1>

                <div
                  style={{
                    borderRadius: '10px',
                    background: '#CED2E9',
                    padding: '10px 16px',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '15px',
                    boxSizing: 'border-box'
                  }}
                >
                  <div
                    className="left-panel-subheader"
                    style={{
                      ...styles.subHeader,
                      textAlign: 'center',
                      marginBottom: 0
                    }}
                  >
                    FIND OUT IF YOU QUALIFY FOR SETTLEMENT
                  </div>
                </div>

                <p className="left-panel-description" style={{...styles.description, textAlign: 'center', width: '100%', marginBottom: '25px'}}>
                  Most accident victims don't realize they may be legally entitled to significant settlement even if the insurance company says otherwise.
                </p>

                <div className="benefits" style={{...styles.benefits, justifyContent: 'center', width: '100%', gap: '15px'}}>
                  <div style={{...styles.benefitItem, textAlign: 'center', justifyContent: 'center', flexDirection: 'column', gap: '8px', minWidth: isMobile ? '80px' : '100px'}}>
                    <img src={lp2i1} alt="Free Case Review" style={{width: isMobile ? '2.5rem' : '3.5rem', height: isMobile ? '2.5rem' : '3.5rem', objectFit: 'contain'}} />
                    <div style={{textAlign: 'center', fontSize: isMobile ? '0.65rem' : '0.7rem'}}>Free<br/>Case Review</div>
                  </div>
                  <div style={{...styles.benefitItem, textAlign: 'center', justifyContent: 'center', flexDirection: 'column', gap: '8px', minWidth: isMobile ? '80px' : '100px'}}>
                    <img src={lp2i2} alt="No Fees" style={{width: isMobile ? '2.5rem' : '3.5rem', height: isMobile ? '2.5rem' : '3.5rem', objectFit: 'contain'}} />
                    <div style={{textAlign: 'center', fontSize: isMobile ? '0.65rem' : '0.7rem'}}>No Fees<br/>unless you win</div>
                  </div>
                  <div style={{...styles.benefitItem, textAlign: 'center', justifyContent: 'center', flexDirection: 'column', gap: '8px', minWidth: isMobile ? '80px' : '100px'}}>
                    <img src={lp2i3} alt="100% Confidential" style={{width: isMobile ? '2.5rem' : '3.5rem', height: isMobile ? '2.5rem' : '3.5rem', objectFit: 'contain'}} />
                    <div style={{textAlign: 'center', fontSize: isMobile ? '0.65rem' : '0.7rem'}}>100% Confidential<br/>Secure Form</div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <h1 className="left-panel-h1" style={styles.h1}>INJURED IN A CAR ACCIDENT?</h1>

                <div
                  style={{
                    borderRadius: '10px',
                    background: '#CED2E9',
                    padding: '10px 16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '8px',
                    marginBottom: '16px'
                  }}
                >
                  <div className="left-panel-subheader" style={{...styles.subHeader, marginBottom: 0}}>
                    FIND OUT IF YOU QUALIFY FOR SETTLEMENT
                  </div>
                </div>

                <p className="left-panel-description" style={styles.description}>
                  Most accident victims don't realize they may be legally entitled to significant settlement even if the insurance company says otherwise.
                </p>

                <div className="benefits" style={styles.benefits}>
                  <div style={styles.benefitItem}>
                    <img src={lp2i1} alt="Free Case Review" style={{width: isMobile ? '2.5rem' : '3.5rem', height: isMobile ? '2.5rem' : '3.5rem', objectFit: 'contain'}} />
                    <div style={{fontSize: isMobile ? '0.65rem' : '0.7rem'}}>Free<br/>Case Review</div>
                  </div>
                  <div style={styles.benefitItem}>
                    <img src={lp2i2} alt="No Fees" style={{width: isMobile ? '2.5rem' : '3.5rem', height: isMobile ? '2.5rem' : '3.5rem', objectFit: 'contain'}} />
                    <div style={{fontSize: isMobile ? '0.65rem' : '0.7rem'}}>No Fees<br/>unless you win</div>
                  </div>
                  <div style={styles.benefitItem}>
                    <img src={lp2i3} alt="100% Confidential" style={{width: isMobile ? '2.5rem' : '3.5rem', height: isMobile ? '2.5rem' : '3.5rem', objectFit: 'contain'}} />
                    <div style={{fontSize: isMobile ? '0.65rem' : '0.7rem'}}>100% Confidential<br/>Secure Form</div>
                  </div>
                </div>
              </>
            )}

            {!isMobile && (
              <>
                <div style={styles.statuteBox}>
                  <div className="left-panel-statute-title" style={styles.statuteTitle}>Statute of Limitations</div>
                  <div className="left-panel-statute-text" style={styles.statuteText}>
                    Failing to file within the deadline may result in losing your right to settlement. No matter how strong your case is.
                  </div>
                </div>

                <div style={{...styles.statuteText, marginBottom: '20px', fontStyle: 'normal'}}>
                  If you are not legally at fault and doesn't have a lawyer yet.<br/>We are here, Get the help you deserve.
                </div>
              </>
            )}

            {/* IMAGE for Desktop only */}
            {!isMobile && (
              <div style={{width: '100%', marginBottom: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <img 
                  src={nimg} 
                  alt="Car accident scene" 
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '200px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    display: 'block'
                  }}
                />
              </div>
            )}

            {/* Start Your Claim Button for Mobile - Bottom */}
            {isMobile && (
              <div style={{width: '100%', marginTop: 'auto', paddingTop: '0px', paddingBottom: '10px', display: 'flex', justifyContent: 'center'}}>
                <button 
                  type="button" 
                  className="btn-primary" 
                  style={{...styles.startClaimButton, marginTop: '0', position: 'relative', top: '-60px'}}
                  onClick={handleStartClaim}
                >
                  Start Your Claim <i className="fa-solid fa-arrow-right"></i>
                </button>
              </div>
            )}
          </div>

          {/* --- RIGHT PANEL --- */}
          <div 
            className="right-panel" 
            style={{
              ...styles.rightPanel,
              display: (isMobile && !isMobileFormStarted) ? 'none' : 'flex'
            }}
          >
            {/* Mobile Header with Logos and Navigation */}
            {isMobile && isMobileFormStarted && (
              <>
                {/* Logos Container */}
                <div style={{
                  width: '100%',
                  padding: '20px 20px 15px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid #e0e0e0'
                }}>
                  <img 
                    src={nimg1} 
                    alt="CLAIM4ACCIDENTS" 
                    style={{width: '130px', height: 'auto', objectFit: 'contain'}} 
                  />
                  <img 
                    src={nimg2} 
                    alt="BELIEF MARKETING" 
                    style={{width: '130px', height: 'auto', objectFit: 'contain'}} 
                  />
                </div>
                
                {/* Navigation Container (Back Button and Step Indicator) */}
                <div style={{
                  width: '100%',
                  padding: '15px 20px 20px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: 'none'
                }}>
                  <button 
                    type="button"
                    style={{
                      ...styles.backButton,
                      fontSize: '1.2rem',
                      color: '#0A1F8F',
                      fontWeight: 700
                    }}
                    onClick={handlePrevious}
                  >
                    <i className="fa-solid fa-arrow-left"></i> Back
                  </button>
                  <div style={{
                    fontSize: '1.2rem',
                    color: '#0A1F8F',
                    fontWeight: 700
                  }}>
                    Step {mobileStep}/3
                  </div>
                </div>
              </>
            )}
            
            {/* Desktop Step Indicator */}
            {!isMobile && (
              <div style={styles.stepIndicator}>
                <span>Step</span>
                <span style={styles.stepCount}>
                  <span style={{color: '#e64a19'}}>{step}</span>/2
                </span>
              </div>
            )}

            {/* MOBILE STEP 1: First 4 Questions */}
            {isMobile && isMobileFormStarted && mobileStep === 1 && (
              <div className="step-1-container" style={{...styles.stepContainer, padding: '20px'}}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Were you involved in an accident</label>
                  <div className="radio-options" style={styles.radioOptions}>
                    <div 
                      className="radio-option" 
                      style={{
                        ...styles.radioOption,
                        ...(formData.involved === 'yes' ? styles.radioOptionSelected : {})
                      }} 
                      onClick={() => handleRadioChange('involved', 'yes')}>
                      <input 
                        type="radio" 
                        id="involved-yes" 
                        name="involved" 
                        checked={formData.involved === 'yes'}
                        onChange={() => handleRadioChange('involved', 'yes')}
                      />
                      <label htmlFor="involved-yes">Yes</label>
                    </div>
                    <div 
                      className="radio-option" 
                      style={{
                        ...styles.radioOption,
                        ...(formData.involved === 'no' ? styles.radioOptionSelected : {})
                      }} 
                      onClick={() => handleRadioChange('involved', 'no')}>
                      <input 
                        type="radio" 
                        id="involved-no" 
                        name="involved"
                        checked={formData.involved === 'no'}
                        onChange={() => handleRadioChange('involved', 'no')}
                      />
                      <label htmlFor="involved-no">No</label>
                    </div>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Were you injured?</label>
                  <div className="radio-options" style={styles.radioOptions}>
                    <div 
                      className="radio-option" 
                      style={{
                        ...styles.radioOption,
                        ...(formData.injured === 'yes' ? styles.radioOptionSelected : {})
                      }} 
                      onClick={() => handleRadioChange('injured', 'yes')}>
                      <input 
                        type="radio" 
                        id="injured-yes" 
                        name="injured"
                        checked={formData.injured === 'yes'}
                        onChange={() => handleRadioChange('injured', 'yes')}
                      />
                      <label htmlFor="injured-yes">Yes</label>
                    </div>
                    <div 
                      className="radio-option" 
                      style={{
                        ...styles.radioOption,
                        ...(formData.injured === 'no' ? styles.radioOptionSelected : {})
                      }} 
                      onClick={() => handleRadioChange('injured', 'no')}>
                      <input 
                        type="radio" 
                        id="injured-no" 
                        name="injured"
                        checked={formData.injured === 'no'}
                        onChange={() => handleRadioChange('injured', 'no')}
                      />
                      <label htmlFor="injured-no">No</label>
                    </div>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Were you legally at fault?</label>
                  <div className="radio-options" style={styles.radioOptions}>
                    <div 
                      className="radio-option" 
                      style={{
                        ...styles.radioOption,
                        ...(formData.fault === 'yes' ? styles.radioOptionSelected : {})
                      }} 
                      onClick={() => handleRadioChange('fault', 'yes')}>
                      <input 
                        type="radio" 
                        id="fault-yes" 
                        name="fault"
                        checked={formData.fault === 'yes'}
                        onChange={() => handleRadioChange('fault', 'yes')}
                      />
                      <label htmlFor="fault-yes">Yes</label>
                    </div>
                    <div 
                      className="radio-option" 
                      style={{
                        ...styles.radioOption,
                        ...(formData.fault === 'no' ? styles.radioOptionSelected : {})
                      }} 
                      onClick={() => handleRadioChange('fault', 'no')}>
                      <input 
                        type="radio" 
                        id="fault-no" 
                        name="fault"
                        checked={formData.fault === 'no'}
                        onChange={() => handleRadioChange('fault', 'no')}
                      />
                      <label htmlFor="fault-no">No</label>
                    </div>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Do you already have an attorney?</label>
                  <div className="radio-options" style={styles.radioOptions}>
                    <div 
                      className="radio-option" 
                      style={{
                        ...styles.radioOption,
                        ...(formData.attorney === 'yes' ? styles.radioOptionSelected : {})
                      }} 
                      onClick={() => handleRadioChange('attorney', 'yes')}>
                      <input 
                        type="radio" 
                        id="attorney-yes" 
                        name="attorney"
                        checked={formData.attorney === 'yes'}
                        onChange={() => handleRadioChange('attorney', 'yes')}
                      />
                      <label htmlFor="attorney-yes">Yes</label>
                    </div>
                    <div 
                      className="radio-option" 
                      style={{
                        ...styles.radioOption,
                        ...(formData.attorney === 'no' ? styles.radioOptionSelected : {})
                      }} 
                      onClick={() => handleRadioChange('attorney', 'no')}>
                      <input 
                        type="radio" 
                        id="attorney-no" 
                        name="attorney"
                        checked={formData.attorney === 'no'}
                        onChange={() => handleRadioChange('attorney', 'no')}
                      />
                      <label htmlFor="attorney-no">No</label>
                    </div>
                  </div>
                </div>

                <div style={{marginTop: 'auto', paddingTop: isMobile ? '0px' : '20px', paddingBottom: isMobile ? '15px' : '0', marginBottom: isMobile ? '-10px' : '0', width: '100%', maxWidth: '100%', boxSizing: 'border-box'}}>
                  <button 
                    type="button" 
                    className="btn-primary" 
                    style={{...styles.btnPrimary, height: isMobile ? '50px' : '60px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', ...(isMobile ? {position: 'relative', top: '-50px'} : {})}} 
                    onClick={handleNext}
                  >
                    Next <i className="fa-solid fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            )}

            {/* MOBILE STEP 2: Time Period Selection */}
            {isMobile && isMobileFormStarted && mobileStep === 2 && (
              <div className="step-2-container" style={{...styles.stepContainer, padding: '20px'}}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>When did the accident happen?</label>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                    {[
                      'In last 1 month', 
                      'In last 3 month', 
                      'In last 6 month', 
                      'In last 1 year'
                    ].map((opt) => (
                      <div 
                        key={opt}
                        style={{
                          ...styles.timeframeOption,
                          borderColor: period === opt ? '#0A1F8F' : '#cfd8dc',
                          backgroundColor: '#ffffff',
                          height: '55px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 20px',
                          borderRadius: '10px'
                        }}
                        onClick={() => setPeriod(opt)}
                      >
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          border: period === opt ? '6px solid #0A1F8F' : '2px solid #cfd8dc',
                          backgroundColor: '#ffffff',
                          marginRight: '15px',
                          boxSizing: 'border-box'
                        }}></div>
                        <span style={{
                          fontSize: '1rem',
                          fontWeight: 500,
                          color: '#0A1F8F'
                        }}>{opt}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{marginTop: 'auto', paddingTop: isMobile ? '0px' : '20px', paddingBottom: isMobile ? '15px' : '0', marginBottom: isMobile ? '-10px' : '0', width: '100%', maxWidth: '100%', boxSizing: 'border-box'}}>
                  <button 
                    type="button" 
                    className="btn-primary" 
                    style={{...styles.btnPrimary, height: isMobile ? '50px' : '60px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', ...(isMobile ? {position: 'relative', top: '-50px'} : {})}} 
                    onClick={handleNext}
                    disabled={!period}
                  >
                    Next <i className="fa-solid fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            )}

            {/* MOBILE STEP 3: Contact Form */}
            {isMobile && isMobileFormStarted && mobileStep === 3 && (
              <div className="step-3-container" style={{...styles.stepContainer, padding: '20px'}}>
                <div style={{...styles.step2Header, textAlign: 'left', marginBottom: '10px'}}>
                  Share Your Information to Get Started
                </div>
                <div style={{...styles.step2Subheader, textAlign: 'left', marginBottom: '20px'}}>
                  You may be entitled to settlement.
                </div>

                <div style={styles.floatingLabelGroup}>
                  <label style={{...styles.floatingLabel, ...(focusedField === 'name' || formData.name ? styles.floatingLabelActive : {})}}>Full Name</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    style={{...(errors.name ? styles.formInputError : styles.formInput), ...styles.floatingInput}} 
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    onFocus={() => setFocusedField('name')}
                    onBlur={(e) => {
                      setFocusedField(null);
                      const error = validateName(e.target.value);
                      setErrors({ ...errors, name: error });
                    }}
                  />
                  {errors.name && <span style={styles.errorMessage}>{errors.name}</span>}
                </div>

                <div style={styles.floatingLabelGroup}>
                  <label style={{...styles.floatingLabel, ...(focusedField === 'phone' || formData.phone ? styles.floatingLabelActive : {})}}>Phone Number</label>
                  <input 
                    type="tel" 
                    className="form-input" 
                    style={{...(errors.phone ? styles.formInputError : styles.formInput), ...styles.floatingInput}} 
                    value={formData.phone}
                    onChange={(e) => {
                      handleInputChange('phone', e.target.value);
                    }}
                    onFocus={() => setFocusedField('phone')}
                    onBlur={(e) => {
                      setFocusedField(null);
                      const error = validatePhone(e.target.value);
                      setErrors({ ...errors, phone: error });
                    }}
                    inputMode="numeric"
                  />
                  {errors.phone && <span style={styles.errorMessage}>{errors.phone}</span>}
                </div>

                <div style={styles.floatingLabelGroup}>
                  <label style={{...styles.floatingLabel, ...(focusedField === 'email' || formData.email ? styles.floatingLabelActive : {})}}>Email Address</label>
                  <input 
                    type="email" 
                    className="form-input" 
                    style={{...(errors.email ? styles.formInputError : styles.formInput), ...styles.floatingInput}} 
                    value={formData.email}
                    onChange={(e) => {
                      handleInputChange('email', e.target.value);
                      triggerEmailUpdate(e.target.value);
                    }}
                    onFocus={() => setFocusedField('email')}
                    onBlur={(e) => {
                      setFocusedField(null);
                      const error = validateEmail(e.target.value);
                      setErrors({ ...errors, email: error });
                    }}
                  />
                  {errors.email && <span style={styles.errorMessage}>{errors.email}</span>}
                </div>

                <div style={styles.checkboxGroup}>
                  <input 
                    type="checkbox" 
                    id="terms" 
                    checked={formData.terms}
                    onChange={(e) => handleCheckboxChange(e.target.checked)}
                    style={{marginTop: '4px', accentColor: '#1a237e', minWidth: '16px', minHeight: '16px'}} 
                  />
                  <label htmlFor="terms" style={styles.legalText}>
                    I agree to the <a href="/PrivacyPolicy" target="_blank" rel="noopener noreferrer" style={{color: '#0A1F8F', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer'}}>Privacy policy</a> and <a href="/Disclaimer" target="_blank" rel="noopener noreferrer" style={{color: '#0A1F8F', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer'}}>Disclaimer</a> and give my express written consent, affiliates and/or lawyer to contact you at the number provided above, even if this number is a wireless number or if I am presently listed on a Do Not Call list. I understand that I may be contacted by telephone, email, text message or mail regarding case options and that I may be called using automatic dialing equipment. Message and data rates may apply. My consent does not require purchase. This is Legal advertising.
                  </label>
                </div>

                <div style={{marginTop: 'auto', paddingTop: isMobile ? '0px' : '20px', paddingBottom: isMobile ? '15px' : '0', marginBottom: isMobile ? '-10px' : '0', width: '100%', maxWidth: '100%', boxSizing: 'border-box'}}>
                  <button 
                    type="button" 
                    className="btn-primary" 
                    style={{
                      ...styles.btnSubmit, 
                      width: '100%',
                      opacity: isSubmitting ? 0.6 : 1, 
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      ...(isMobile ? {position: 'relative', top: '-50px'} : {})
                    }} 
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i> Submitting...
                      </>
                    ) : (
                      <>
                        Submit Now <i className="fa-solid fa-arrow-right"></i>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* DESKTOP STEP 1: All Questions */}
            {!isMobile && step === 1 && (
              <div className="step-1-container" style={styles.stepContainer}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Were you involved in an accident?</label>
                  <div className="radio-options" style={styles.radioOptions}>
                    <div className="radio-option" style={styles.radioOption} onClick={(e) => {
                      const input = e.currentTarget.querySelector('input[type="radio"]');
                      if (input && input.onChange) {
                        input.onChange();
                      } else if (input) {
                        const id = input.id || '';
                        const field = id.replace(/-yes|-no|-desktop|-mobile/g, '');
                        const value = id.includes('-yes') ? 'yes' : id.includes('-no') ? 'no' : input.value;
                        if (field && value) {
                          handleRadioChange(field, value);
                        }
                      }
                    }}>
                      <input 
                        type="radio" 
                        id="involved-yes" 
                        name="involved" 
                        checked={formData.involved === 'yes'}
                        onChange={() => handleRadioChange('involved', 'yes')}
                      />
                      <label htmlFor="involved-yes">Yes</label>
                    </div>
                    <div className="radio-option" style={styles.radioOption} onClick={(e) => {
                      const input = e.currentTarget.querySelector('input[type="radio"]');
                      if (input && input.onChange) {
                        input.onChange();
                      } else if (input) {
                        const id = input.id || '';
                        const field = id.replace(/-yes|-no|-desktop|-mobile/g, '');
                        const value = id.includes('-yes') ? 'yes' : id.includes('-no') ? 'no' : input.value;
                        if (field && value) {
                          handleRadioChange(field, value);
                        }
                      }
                    }}>
                      <input 
                        type="radio" 
                        id="involved-no" 
                        name="involved"
                        checked={formData.involved === 'no'}
                        onChange={() => handleRadioChange('involved', 'no')}
                      />
                      <label htmlFor="involved-no">No</label>
                    </div>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Were you injured?</label>
                  <div className="radio-options" style={styles.radioOptions}>
                    <div className="radio-option" style={styles.radioOption} onClick={(e) => {
                      const input = e.currentTarget.querySelector('input[type="radio"]');
                      if (input && input.onChange) {
                        input.onChange();
                      } else if (input) {
                        const id = input.id || '';
                        const field = id.replace(/-yes|-no|-desktop|-mobile/g, '');
                        const value = id.includes('-yes') ? 'yes' : id.includes('-no') ? 'no' : input.value;
                        if (field && value) {
                          handleRadioChange(field, value);
                        }
                      }
                    }}>
                      <input 
                        type="radio" 
                        id="injured-yes" 
                        name="injured"
                        checked={formData.injured === 'yes'}
                        onChange={() => handleRadioChange('injured', 'yes')}
                      />
                      <label htmlFor="injured-yes">Yes</label>
                    </div>
                    <div className="radio-option" style={styles.radioOption} onClick={(e) => {
                      const input = e.currentTarget.querySelector('input[type="radio"]');
                      if (input && input.onChange) {
                        input.onChange();
                      } else if (input) {
                        const id = input.id || '';
                        const field = id.replace(/-yes|-no|-desktop|-mobile/g, '');
                        const value = id.includes('-yes') ? 'yes' : id.includes('-no') ? 'no' : input.value;
                        if (field && value) {
                          handleRadioChange(field, value);
                        }
                      }
                    }}>
                      <input 
                        type="radio" 
                        id="injured-no" 
                        name="injured"
                        checked={formData.injured === 'no'}
                        onChange={() => handleRadioChange('injured', 'no')}
                      />
                      <label htmlFor="injured-no">No</label>
                    </div>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Were you legally at fault?</label>
                  <div className="radio-options" style={styles.radioOptions}>
                    <div className="radio-option" style={styles.radioOption} onClick={(e) => {
                      const input = e.currentTarget.querySelector('input[type="radio"]');
                      if (input && input.onChange) {
                        input.onChange();
                      } else if (input) {
                        const id = input.id || '';
                        const field = id.replace(/-yes|-no|-desktop|-mobile/g, '');
                        const value = id.includes('-yes') ? 'yes' : id.includes('-no') ? 'no' : input.value;
                        if (field && value) {
                          handleRadioChange(field, value);
                        }
                      }
                    }}>
                      <input 
                        type="radio" 
                        id="fault-yes" 
                        name="fault"
                        checked={formData.fault === 'yes'}
                        onChange={() => handleRadioChange('fault', 'yes')}
                      />
                      <label htmlFor="fault-yes">Yes</label>
                    </div>
                    <div className="radio-option" style={styles.radioOption} onClick={(e) => {
                      const input = e.currentTarget.querySelector('input[type="radio"]');
                      if (input && input.onChange) {
                        input.onChange();
                      } else if (input) {
                        const id = input.id || '';
                        const field = id.replace(/-yes|-no|-desktop|-mobile/g, '');
                        const value = id.includes('-yes') ? 'yes' : id.includes('-no') ? 'no' : input.value;
                        if (field && value) {
                          handleRadioChange(field, value);
                        }
                      }
                    }}>
                      <input 
                        type="radio" 
                        id="fault-no" 
                        name="fault"
                        checked={formData.fault === 'no'}
                        onChange={() => handleRadioChange('fault', 'no')}
                      />
                      <label htmlFor="fault-no">No</label>
                    </div>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Do you already have an attorney?</label>
                  <div className="radio-options" style={styles.radioOptions}>
                    <div className="radio-option" style={styles.radioOption} onClick={(e) => {
                      const input = e.currentTarget.querySelector('input[type="radio"]');
                      if (input && input.onChange) {
                        input.onChange();
                      } else if (input) {
                        const id = input.id || '';
                        const field = id.replace(/-yes|-no|-desktop|-mobile/g, '');
                        const value = id.includes('-yes') ? 'yes' : id.includes('-no') ? 'no' : input.value;
                        if (field && value) {
                          handleRadioChange(field, value);
                        }
                      }
                    }}>
                      <input 
                        type="radio" 
                        id="attorney-yes" 
                        name="attorney"
                        checked={formData.attorney === 'yes'}
                        onChange={() => handleRadioChange('attorney', 'yes')}
                      />
                      <label htmlFor="attorney-yes">Yes</label>
                    </div>
                    <div className="radio-option" style={styles.radioOption} onClick={(e) => {
                      const input = e.currentTarget.querySelector('input[type="radio"]');
                      if (input && input.onChange) {
                        input.onChange();
                      } else if (input) {
                        const id = input.id || '';
                        const field = id.replace(/-yes|-no|-desktop|-mobile/g, '');
                        const value = id.includes('-yes') ? 'yes' : id.includes('-no') ? 'no' : input.value;
                        if (field && value) {
                          handleRadioChange(field, value);
                        }
                      }
                    }}>
                      <input 
                        type="radio" 
                        id="attorney-no" 
                        name="attorney"
                        checked={formData.attorney === 'no'}
                        onChange={() => handleRadioChange('attorney', 'no')}
                      />
                      <label htmlFor="attorney-no">No</label>
                    </div>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>When did the accident happen?</label>
                  <div style={styles.customSelectWrapper} ref={dropdownRef}>
                    <div style={styles.customSelectTrigger} onClick={toggleDropdown}>
                      <span>{period || 'Select timeframe'}</span>
                      <div style={styles.triggerArrow}>
                        <i className="fa-solid fa-chevron-down" style={{color: 'white', fontSize: '0.7rem'}}></i>
                      </div>
                    </div>
                    <div style={styles.customOptions}>
                      {['In last 1 month', 'In last 3 months', 'In last 6 months', 'In last 1 year'].map((opt) => (
                        <div 
                          key={opt}
                          className={`custom-option ${period === opt ? 'selected' : ''}`}
                          onClick={() => selectOption(opt)}
                        >
                          <div className="radio-circle"></div>
                          <span>{opt}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{marginTop: 'auto', paddingTop: isMobile ? '0px' : '20px', paddingBottom: isMobile ? '15px' : '0', marginBottom: isMobile ? '-10px' : '0', width: '100%', maxWidth: '100%', boxSizing: 'border-box'}}>
                  <button type="button" className="btn-primary" style={{...styles.btnPrimary, ...(isMobile ? {position: 'relative', top: '-50px'} : {})}} onClick={handleNext}>
                    Next Step <i className="fa-solid fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            )}

            {/* DESKTOP STEP 2: Contact Form */}
            {!isMobile && step === 2 && (
              <div className="step-2-container fade-in" style={{...styles.stepContainer, animation: 'fadeIn 0.5s'}}>
                <div style={styles.step2Header}>Were you injured in a motor vehicle accident?</div>
                <div style={styles.step2Subheader}>You may be entitled to settlement.</div>

                <div style={styles.floatingLabelGroup}>
                  <label style={{...styles.floatingLabel, ...(focusedField === 'name' || formData.name ? styles.floatingLabelActive : {})}}>Full Name</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    style={{...(errors.name ? styles.formInputError : styles.formInput), ...styles.floatingInput}} 
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    onFocus={() => setFocusedField('name')}
                    onBlur={(e) => {
                      setFocusedField(null);
                      const error = validateName(e.target.value);
                      setErrors({ ...errors, name: error });
                    }}
                  />
                  {errors.name && <span style={styles.errorMessage}>{errors.name}</span>}
                </div>

                <div style={styles.floatingLabelGroup}>
                  <label style={{...styles.floatingLabel, ...(focusedField === 'phone' || formData.phone ? styles.floatingLabelActive : {})}}>Phone Number</label>
                  <input 
                    type="tel" 
                    className="form-input" 
                    style={{...(errors.phone ? styles.formInputError : styles.formInput), ...styles.floatingInput}} 
                    value={formData.phone}
                    onChange={(e) => {
                      handleInputChange('phone', e.target.value);
                    }}
                    onFocus={() => setFocusedField('phone')}
                    onBlur={(e) => {
                      setFocusedField(null);
                      const error = validatePhone(e.target.value);
                      setErrors({ ...errors, phone: error });
                    }}
                    inputMode="numeric"
                  />
                  {errors.phone && <span style={styles.errorMessage}>{errors.phone}</span>}
                </div>

                <div style={styles.floatingLabelGroup}>
                  <label style={{...styles.floatingLabel, ...(focusedField === 'email' || formData.email ? styles.floatingLabelActive : {})}}>Email Address</label>
                  <input 
                    type="email" 
                    className="form-input" 
                    style={{...(errors.email ? styles.formInputError : styles.formInput), ...styles.floatingInput}} 
                    value={formData.email}
                    onChange={(e) => {
                      handleInputChange('email', e.target.value);
                      triggerEmailUpdate(e.target.value);
                    }}
                    onFocus={() => setFocusedField('email')}
                    onBlur={(e) => {
                      setFocusedField(null);
                      const error = validateEmail(e.target.value);
                      setErrors({ ...errors, email: error });
                    }}
                  />
                  {errors.email && <span style={styles.errorMessage}>{errors.email}</span>}
                </div>

                <div style={styles.checkboxGroup}>
                  <input 
                    type="checkbox" 
                    id="terms" 
                    checked={formData.terms}
                    onChange={(e) => handleCheckboxChange(e.target.checked)}
                    style={{marginTop: '4px', accentColor: '#1a237e', minWidth: '16px', minHeight: '16px'}} 
                  />
                  <label htmlFor="terms" style={styles.legalText}>
                    I agree to the <a href="/PrivacyPolicy" target="_blank" rel="noopener noreferrer" style={{color: '#0A1F8F', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer'}}>Privacy policy</a> and <a href="/Disclaimer" target="_blank" rel="noopener noreferrer" style={{color: '#0A1F8F', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer'}}>Disclaimer</a> and give my express written consent, affiliates and/or lawyer to contact you at the number provided above, even if this number is a wireless number or if I am presently listed on a Do Not Call list. I understand that I may be contacted by telephone, email, text message or mail regarding case options and that I may be called using automatic dialing equipment. Message and data rates may apply. My consent does not require purchase. This is Legal advertising.
                  </label>
                </div>

                <div style={styles.buttonContainer}>
                  <button type="button" className="btn-primary" style={{...styles.btnPrevious, ...(isMobile ? {position: 'relative', top: '-50px'} : {})}} onClick={handlePrevious} disabled={isSubmitting}>
                    <i className="fa-solid fa-arrow-left"></i> Previous
                  </button>
                  <button 
                    type="button" 
                    className="btn-primary" 
                    style={{...styles.btnSubmit, opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer', ...(isMobile ? {position: 'relative', top: '-50px'} : {})}} 
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i> Submitting...
                      </>
                    ) : (
                      <>
                        Submit Now <i className="fa-solid fa-arrow-right"></i>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimForm;